import { usePhrase, useServerErrors } from '@/hooks';
import React, { ReactNode } from 'react';
import { Button, Grid, Paper } from '@mui/material';
import { ProfessionalInfo, ProfessionalInfoSubmit, VerificationInfo } from '@/types/types';
import { ProfessionalInfoForm } from './ProfessionalInfoForm';
import { DocumentsForm } from './DocumentsForm/DocumentsForm';
import { VERIFICATION_PROFESSIONAL_INFO_PHRASES } from '@/consts/phrases/verificationPhrases';
import { useForm } from 'react-hook-form';
import { verificationService } from '@/services/api';
import { config } from '@/config';
import { mutate } from 'swr';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';

const API_URL = config.api.url.verification;
const FIELD_PREFIX = 'professional-info-';
const startsWithFieldPrefix = (x: string) => x.startsWith(FIELD_PREFIX);
const removeFieldPrefix = (x: string) => x.substring(FIELD_PREFIX.length);
const prependFieldPrefix = (x: string) => `${FIELD_PREFIX}${x}`;

const buildRequestFromFields = (formData: Record<string, string>) =>
    Object.entries(formData).reduce(
        (acc, [key, value]) =>
            startsWithFieldPrefix(key)
                ? [...acc, { id: parseInt(removeFieldPrefix(key)), value }]
                : acc,
        [] as ProfessionalInfoSubmit[],
    );

const extractDefaultProfInfoValues = (professionalInfo: ProfessionalInfo[]) =>
    professionalInfo.reduce(
        (response, current) => ({
            ...response,
            [prependFieldPrefix(`${current.id}`)]: current.value || '',
        }),
        {} as Record<string, string>,
    );

const FormElementWrapper = (props: { children: ReactNode }) => {
    return (
        <Grid
            size={{ xs: 12, sm: 6 }}
            className="verificationFormElementWrapper"
            sx={{
                p: 1,
            }}
        >
            <Grid size={12} className="verificationFormElementBlock" sx={{ p: 0, pb: 1 }}>
                {props.children}
            </Grid>
        </Grid>
    );
};

export const VerificationForms = (props: {
    requestToken: string;
    verificationInfo: VerificationInfo;
    isOnlineIdentityVerificationOnly: boolean;
}) => {
    const { t } = usePhrase();
    const { professionalInfo } = props.verificationInfo;

    const { control, getValues, setError, clearErrors, formState } = useForm<
        Partial<VerificationInfo>
    >({
        defaultValues: extractDefaultProfInfoValues(professionalInfo),
    });
    const { errors } = formState;

    const { handleServerErrors, serverErrors, clearGeneralErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const handleSubmitClicked = async (): Promise<void> => {
        const formData = getValues();
        const professionalInfo = buildRequestFromFields(
            formData as unknown as Record<string, string>,
        );
        try {
            await verificationService.saveProfessionalInfo(props.requestToken, professionalInfo);
            await mutate(`${API_URL}/${props.requestToken}`);
            clearErrors();
            clearGeneralErrors();
        } catch (e: unknown) {
            handleServerErrors(e);
        }
    };

    const formIsEnabled = ['pending-info-from-user', 'not-verified'].includes(
        props.verificationInfo?.stage,
    );

    return (
        <>
            {!props.isOnlineIdentityVerificationOnly && (
                <Paper sx={{ p: 2, mt: 3, width: '100%' }}>
                    <Grid container>
                        <FormGridErrors
                            errors={serverErrors}
                            gridSx={{ color: 'red', textAlign: 'center', mb: 2 }}
                            generalErrorId="message_box"
                            generalErrorTestId="error-message"
                        />
                    </Grid>

                    <Grid
                        container
                        size={12}
                        sx={{
                            justifyContent: professionalInfo.length ? 'space-between' : 'center',
                        }}
                    >
                        {!!professionalInfo.length && (
                            <FormElementWrapper>
                                <ProfessionalInfoForm
                                    control={control}
                                    requestToken={props.requestToken}
                                    verificationInfo={props.verificationInfo}
                                    isEnabled={formIsEnabled}
                                    errors={errors}
                                />
                            </FormElementWrapper>
                        )}
                        {
                            <FormElementWrapper>
                                <DocumentsForm
                                    key={props.verificationInfo.documents.length}
                                    requestToken={props.requestToken}
                                    verificationInfo={props.verificationInfo}
                                    isEnabled={formIsEnabled}
                                />
                            </FormElementWrapper>
                        }
                        <Grid
                            className="verificationSubmitButtonWrapper"
                            size={12}
                            container
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Button
                                type="button"
                                size="large"
                                color="primary"
                                variant="contained"
                                disabled={!formIsEnabled}
                                data-testid="form-submit"
                                onClick={handleSubmitClicked}
                            >
                                {t(VERIFICATION_PROFESSIONAL_INFO_PHRASES.SUBMIT)}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}
        </>
    );
};
